import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../utils/firebaseConfig.js";
import { FaTrash, FaPlus, FaPencilAlt, FaTimes } from "react-icons/fa";
import "../Styles/Admin.css";

const MAX_FILE_BYTES = 8 * 1024 * 1024; // 8 MB
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_DISHES = 10;

const EMPTY_FORM = {
  name: "",
  tagline: "",
  story: "",
  ingredients: "",
  price: "",
  dietary: "",
  pairing: "",
  chefNote: "",
  order: "",
};

const byOrder = (a, b) => {
  const ao = typeof a.order === "number" ? a.order : Number.MAX_SAFE_INTEGER;
  const bo = typeof b.order === "number" ? b.order : Number.MAX_SAFE_INTEGER;
  if (ao !== bo) return ao - bo;
  const at = a.createdAt?.seconds ?? 0;
  const bt = b.createdAt?.seconds ?? 0;
  return at - bt;
};

const ManageSignatureDishes = () => {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingImage, setEditingImage] = useState(null); // existing image url while editing
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const formRef = useRef(null);

  const isEditing = editingId !== null;
  const reachedMax = dishes.length >= MAX_DISHES && !isEditing;

  const refreshDishes = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "signatureDishes"));
      const docs = [];
      snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
      docs.sort(byOrder);
      setDishes(docs);
    } catch (err) {
      console.log("Error loading signature dishes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshDishes();
  }, []);

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setFile(null);
    setEditingId(null);
    setEditingImage(null);
    setErrorMsg("");
    const input = document.getElementById("manage-dishes-file-input");
    if (input) input.value = "";
  };

  const handleFileChange = (e) => {
    setErrorMsg("");
    const f = e.target.files && e.target.files[0];
    if (!f) {
      setFile(null);
      return;
    }
    if (!ACCEPTED.includes(f.type)) {
      setErrorMsg("Επιτρέπονται μόνο εικόνες (JPG, PNG, WEBP, GIF).");
      setFile(null);
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setErrorMsg("Η εικόνα ξεπερνά τα 8 MB.");
      setFile(null);
      return;
    }
    setFile(f);
  };

  const startEdit = (dish) => {
    setEditingId(dish.id);
    setEditingImage(dish.image || null);
    setForm({
      name: dish.name || "",
      tagline: dish.tagline || "",
      story: dish.story || "",
      ingredients: dish.ingredients || "",
      price: dish.price || "",
      dietary: dish.dietary || "",
      pairing: dish.pairing || "",
      chefNote: dish.chefNote || "",
      order: typeof dish.order === "number" ? String(dish.order) : "",
    });
    setFile(null);
    setErrorMsg("");
    const input = document.getElementById("manage-dishes-file-input");
    if (input) input.value = "";
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setErrorMsg("Συμπληρώστε όνομα πιάτου.");
      return;
    }
    if (!isEditing && !file) {
      setErrorMsg("Επιλέξτε εικόνα για το πιάτο.");
      return;
    }

    setSaving(true);
    setErrorMsg("");

    try {
      // Shared fields for both create and update.
      const parsedOrder = parseInt(form.order, 10);
      const payload = {
        name: form.name.trim(),
        tagline: form.tagline.trim(),
        story: form.story.trim(),
        ingredients: form.ingredients.trim(),
        price: form.price.trim(),
        dietary: form.dietary.trim(),
        pairing: form.pairing.trim(),
        chefNote: form.chefNote.trim(),
        order: Number.isFinite(parsedOrder) ? parsedOrder : null,
      };

      let newStoragePath = null;
      let newUrl = null;
      if (file) {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        newStoragePath = `dishes/${Date.now()}_${safeName}`;
        const storageRef = ref(storage, newStoragePath);
        await uploadBytes(storageRef, file);
        newUrl = await getDownloadURL(storageRef);
      }

      if (isEditing) {
        const updateData = { ...payload };
        if (newUrl) {
          updateData.image = newUrl;
          updateData.storagePath = newStoragePath;
        }
        await updateDoc(doc(db, "signatureDishes", editingId), updateData);

        // Remove the previous image from Storage once the doc points elsewhere.
        if (newUrl) {
          const previous = dishes.find((d) => d.id === editingId);
          if (previous?.storagePath) {
            try {
              await deleteObject(ref(storage, previous.storagePath));
            } catch (err) {
              console.log("Old image delete warning:", err);
            }
          }
        }
      } else {
        await addDoc(collection(db, "signatureDishes"), {
          ...payload,
          image: newUrl,
          storagePath: newStoragePath,
          createdAt: serverTimestamp(),
        });
      }

      resetForm();
      await refreshDishes();
    } catch (err) {
      console.log("Error saving signature dish:", err);
      setErrorMsg("Σφάλμα κατά την αποθήκευση. Δοκιμάστε ξανά.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dish) => {
    const confirmed = window.confirm(`Διαγραφή πιάτου «${dish.name}»;`);
    if (!confirmed) return;
    try {
      if (dish.storagePath) {
        try {
          await deleteObject(ref(storage, dish.storagePath));
        } catch (err) {
          console.log("Storage delete warning:", err);
        }
      }
      await deleteDoc(doc(db, "signatureDishes", dish.id));
      if (editingId === dish.id) resetForm();
      await refreshDishes();
    } catch (err) {
      console.log("Error deleting signature dish:", err);
    }
  };

  return (
    <div className="manage_dishes">
      <form className="manage_dishes_form" onSubmit={handleSubmit} ref={formRef}>
        <p className="manage_dishes_form_title">
          {isEditing ? "Επεξεργασία πιάτου" : "Νέο πιάτο-σήμα"}
          <span className="manage_dishes_counter">
            {dishes.length}/{MAX_DISHES}
          </span>
        </p>

        {reachedMax ? (
          <p className="manage_dishes_max">
            Έχετε ήδη {MAX_DISHES} πιάτα-σήμα. Διαγράψτε κάποιο για να
            προσθέσετε νέο.
          </p>
        ) : (
          <>
            <div className="manage_dishes_row">
              <label
                htmlFor="manage-dishes-file-input"
                className="file-picker"
              >
                <FaPlus />
                <span>
                  {file
                    ? file.name
                    : isEditing
                    ? "Αλλαγη φωτο (προαιρετικο)"
                    : "Φωτο πιατου"}
                </span>
                <input
                  id="manage-dishes-file-input"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={saving}
                />
              </label>

              {isEditing && editingImage && !file && (
                <img
                  src={editingImage}
                  alt=""
                  className="manage_dishes_current_thumb"
                />
              )}
            </div>

            <input
              type="text"
              placeholder="Όνομα πιάτου *"
              value={form.name}
              onChange={setField("name")}
              disabled={saving}
              maxLength={80}
            />

            <input
              type="text"
              placeholder="Σύντομη περιγραφή (tagline)"
              value={form.tagline}
              onChange={setField("tagline")}
              disabled={saving}
              maxLength={140}
            />

            <textarea
              placeholder="Περιγραφή / ιστορία του πιάτου"
              value={form.story}
              onChange={setField("story")}
              disabled={saving}
              rows={5}
            />

            <input
              type="text"
              placeholder="Υλικά (χωρισμένα με κόμμα)"
              value={form.ingredients}
              onChange={setField("ingredients")}
              disabled={saving}
              maxLength={300}
            />

            <div className="manage_dishes_row">
              <input
                type="text"
                placeholder="Τιμή (π.χ. €14)"
                value={form.price}
                onChange={setField("price")}
                disabled={saving}
                maxLength={20}
              />
              <input
                type="number"
                placeholder="Σειρά (1, 2, 3…)"
                value={form.order}
                onChange={setField("order")}
                disabled={saving}
                min={0}
              />
            </div>

            <input
              type="text"
              placeholder="Διατροφικά / αλλεργιογόνα (με κόμμα)"
              value={form.dietary}
              onChange={setField("dietary")}
              disabled={saving}
              maxLength={200}
            />

            <input
              type="text"
              placeholder="Συνοδεύεται με (κρασί/ποτό)"
              value={form.pairing}
              onChange={setField("pairing")}
              disabled={saving}
              maxLength={140}
            />

            <textarea
              placeholder="Σχόλιο του σεφ"
              value={form.chefNote}
              onChange={setField("chefNote")}
              disabled={saving}
              rows={3}
            />

            <div className="manage_dishes_actions">
              <button type="submit" disabled={saving}>
                {saving
                  ? "Αποθηκευση..."
                  : isEditing
                  ? "Αποθηκευση αλλαγων"
                  : "Προσθηκη πιατου"}
              </button>
              {isEditing && (
                <button
                  type="button"
                  className="manage_dishes_cancel"
                  onClick={resetForm}
                  disabled={saving}
                >
                  <FaTimes /> Ακυρωση
                </button>
              )}
            </div>

            {errorMsg && <p className="manage_photos_error">{errorMsg}</p>}
          </>
        )}
      </form>

      <hr />

      {loading ? (
        <p>Loading...</p>
      ) : dishes.length === 0 ? (
        <p className="manage_photos_empty">Δεν υπάρχουν πιάτα-σήμα ακόμα.</p>
      ) : (
        <ul className="manage_dishes_list">
          {dishes.map((dish) => (
            <li
              key={dish.id}
              className={editingId === dish.id ? "is-editing" : ""}
            >
              {dish.image ? (
                <img src={dish.image} alt={dish.name || ""} />
              ) : (
                <div className="manage_dishes_noimg" />
              )}
              <div className="manage_dishes_meta">
                <span className="manage_dishes_name">
                  {dish.name || <em>χωρίς όνομα</em>}
                </span>
                {dish.tagline && (
                  <span className="manage_dishes_tagline">{dish.tagline}</span>
                )}
                <div className="manage_dishes_item_actions">
                  <button
                    type="button"
                    className="manage_dishes_edit"
                    onClick={() => startEdit(dish)}
                    aria-label="Επεξεργασία"
                  >
                    <FaPencilAlt />
                  </button>
                  <button
                    type="button"
                    className="manage_photos_delete"
                    onClick={() => handleDelete(dish)}
                    aria-label="Διαγραφή"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ManageSignatureDishes;
